import { createElement, type ComponentPropsWithoutRef, type ReactNode } from 'react';

export type CardHeadingLevel = 2 | 3 | 4 | 5 | 6;

/** Design-system elevation levels mapped to `.card--*` modifiers in theme.css. */
export type CardElevation = 'flat' | 'soft' | 'card' | 'pop';

export type CardProps = ComponentPropsWithoutRef<'section'> & {
  title?: ReactNode;
  /** Heading level for the optional title (default 3). */
  headingLevel?: CardHeadingLevel;
  /**
   * Elevation level. Defaults to `card` (design-system hero/modal shadow).
   * `flat` uses a border only; `soft` and `pop` map to `--shadow-soft` / `--shadow-pop`.
   */
  elevation?: CardElevation;
  children: ReactNode;
};

/** Container surface for grouping related content with an optional title. */
export function Card({
  title,
  headingLevel = 3,
  elevation = 'card',
  children,
  className,
  ...props
}: CardProps): React.JSX.Element {
  const elevationClass = elevation === 'card' ? undefined : `card--${elevation}`;
  const classes = ['card', elevationClass, className].filter(Boolean).join(' ');

  return (
    <section className={classes} {...props}>
      {title != null && title !== ''
        ? createElement(`h${headingLevel}`, { className: 'card__title' }, title)
        : null}
      {children}
    </section>
  );
}

export type CardButtonTone = 'default' | 'info';

export type CardButtonProps = ComponentPropsWithoutRef<'button'> & {
  /** Elevation at rest. Defaults to `flat`, the bordered surface a list of choices sits on. */
  elevation?: CardElevation;
  /** Marks the chosen card in a set (`aria-current`). Drawn as a ring outside the card, so its size never moves. */
  selected?: boolean;
  /** `info` is the soft brand-blue wash of the "up next" pattern, for the card a set leads with. */
  tone?: CardButtonTone;
  children: ReactNode;
};

/**
 * A whole card that is one selectable control — one of a set the user picks between, such as a
 * version in a history timeline. Content must be phrasing elements (`span`), as inside any button.
 */
export function CardButton({
  elevation = 'flat',
  selected = false,
  tone = 'default',
  type = 'button',
  children,
  className,
  ...props
}: CardButtonProps): React.JSX.Element {
  const classes = [
    'card',
    elevation === 'card' ? undefined : `card--${elevation}`,
    'card--interactive',
    tone === 'info' ? 'card--tone-info' : undefined,
    selected ? 'card--selected' : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // `aria-current`, not `aria-pressed`: the set is single-select, and a pressed toggle tells a
    // screen reader each card can be switched on and off on its own. Spread first so a caller's
    // props cannot silently drop the state.
    <button {...props} type={type} aria-current={selected ? 'true' : undefined} className={classes}>
      {children}
    </button>
  );
}
